import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/browser';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { listenToLogoutFromOtherTabs, notifyLogoutToOtherTabs } from '@docorga/common';
import { Docorga } from '@docorga-v1/docorga-common';

import { StructureMemberResource } from '../../shared/resources/structureMember.resource';
import { environment } from '@environments/environment';

type KeycloakAction = 'REGISTER' | 'UPDATE_EMAIL' | 'UPDATE_PASSWORD';
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly KEYCLOAK_BASEURL = environment.keycloakUrl;
  private readonly KEYCLOAK_REALM = environment.keycloakRealm;
  private readonly KEYCLOAK_MIDURL = `realms/${this.KEYCLOAK_REALM}/protocol/openid-connect/`;
  private readonly KEYCLOAK_CLIENT_ID = 'dcrg-patient-cli';
  private readonly currentUserSubject = new BehaviorSubject(
    {} as Docorga.interfaces.structureMember.IStructureMemberPublicJson
  );
  public currentUser = this.currentUserSubject.asObservable().pipe(distinctUntilChanged());

  private readonly isAuthenticatedSubject = new BehaviorSubject(false);
  private readonly isCompletedSubject = new BehaviorSubject(null);
  public isAuthenticated = this.isAuthenticatedSubject.asObservable();
  public isCompleted = this.isCompletedSubject.asObservable();

  constructor(
    private readonly StructureMemberResource: StructureMemberResource,
  ) {}

  private sendAction(action: KeycloakAction): void {
    const redirect_uri = globalThis.location.origin + `/mySpace/account/settings`

    //Use remove_credential instead of update_password. Deprecated update_password
    const url =
      this.KEYCLOAK_BASEURL +
      this.KEYCLOAK_MIDURL +
      'auth' +
      `?response_type=code&scope=openid&client_id=${this.KEYCLOAK_CLIENT_ID}&redirect_uri=${redirect_uri}&kc_action=${action}`;
    window.open(url, '_self');
  }

  // /mySpace needs a session: reload so forwardAuth redirects to sign-in. Elsewhere only the header menu changes
  listenToLogoutFromOtherTabs() {
    listenToLogoutFromOtherTabs(() => {
      if (globalThis.location.pathname.startsWith('/mySpace')) {
        globalThis.location.reload();
      } else {
        this.purgeAuth();
      }
    });
  }

  populate() {
    this.StructureMemberResource.getStructureMember().subscribe({
      next: (data: Docorga.interfaces.structureMember.IStructureMemberPublicJson) => {
        this.setAuth(data);
      },
    });
  }

  purgeAuth() {
    // Set current user to an empty object
    this.currentUserSubject.next({} as Docorga.interfaces.structureMember.IStructureMemberPublicJson);
    // Set auth status to false
    this.isAuthenticatedSubject.next(false);
    this.isCompletedSubject.next(false);
  }

  getCurrentUser(): Docorga.interfaces.structureMember.IStructureMemberPublicJson {
    return this.currentUserSubject.value;
  }

  setAuth(structureMember: Docorga.interfaces.structureMember.IStructureMemberPublicJson) {
    this.isAuthenticatedSubject.next(true);
    this.isCompletedSubject.next(!!structureMember._id);
    // Set current user data into observable
    this.currentUserSubject.next(structureMember);

    Sentry.setUser({
      id: structureMember._id.toString(),
      email: structureMember.email,
      username: `${structureMember.firstName} ${structureMember.lastName}`
    });
  }

  signIn() {
    this.purgeAuth();
    globalThis.location.href = globalThis.location.origin + `/oauth2/sign_in`;
  }

  register() {
    this.purgeAuth();
    const redirect_uri = globalThis.location.origin;

    //Use remove_credential instead of update_password. Deprecated update_password
    const url =
      this.KEYCLOAK_BASEURL +
      this.KEYCLOAK_MIDURL +
      'registrations' +
      `?response_type=code&scope=openid&client_id=${this.KEYCLOAK_CLIENT_ID}&redirect_uri=${redirect_uri}`;
    window.open(url, '_self');
  }

  createStructure(structureMember: Docorga.interfaces.structure.IStructureCreateJson['structureMember']) {
    return this.StructureMemberResource.createStructure(structureMember).pipe(
      map((data) => {
        if (data.isSuccess) {
          this.setAuth(data.structureMember);
        }
        return data;
      })
    );
  }

  logout() {
    this.purgeAuth();
    notifyLogoutToOtherTabs();
    globalThis.location.href = globalThis.location.origin + '/oauth2/sign_out';
  }

  public updatePassword() {
    this.sendAction('UPDATE_PASSWORD');
  }

  public updateEmail() {
    this.sendAction('UPDATE_EMAIL');
  }
}
